/**
 * CrmInboxGroupFilter - Filtro de Grupo de Inbox para o Dashboard de Conversas
 *
 * Mesmo padrão visual do CrmInboxFilter (Popover + Command).
 * Quando um grupo é selecionado, o Dashboard filtra todas as métricas pelas
 * inboxes vinculadas àquele grupo.
 *
 * @version 1.0.0
 * RELEASE: 2026-10-05 v1.1.0 - Gatilho do filtro segue o modelo de pill: sem a linha cinza da borda, fundo de superfície, sombra sutil e cantos arredondados (rounded-full).
 * RELEASE: 2026-06-01 v1.0.0 - Criação inicial
 */

import { useState } from 'react';
import { Check, ChevronDown, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Skeleton } from '@/components/ui/skeleton';
import { useInboxGroups } from '@/hooks/useInboxGroups';

interface CrmInboxGroupFilterProps {
  value: string | null;
  onChange: (groupId: string | null) => void;
  className?: string;
}

export function CrmInboxGroupFilter({ value, onChange, className }: CrmInboxGroupFilterProps) {
  const [open, setOpen] = useState(false);
  // Apenas grupos ativos aparecem como opção de filtro
  const { data: groups = [], isLoading } = useInboxGroups(true);

  const selected = groups.find((g) => g.id === value);

  const handleSelect = (groupId: string | null) => {
    onChange(groupId);
    setOpen(false);
  };

  // Detecta versão compacta pela classe (mesmo padrão dos outros filtros)
  const isCompact = className?.includes('h-[22px]');

  if (isLoading) {
    return <Skeleton className={cn(isCompact ? 'h-[22px] w-[100px]' : 'h-9 w-[160px]')} />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('h-9 px-3 gap-2 font-normal border-0 bg-background rounded-full', className)}
        >
          {selected ? (
            <>
              <span
                className={cn('rounded-full shrink-0', isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3')}
                style={{ backgroundColor: selected.color }}
              />
              <span
                className={cn(
                  'truncate hidden sm:inline',
                  isCompact ? 'max-w-[60px]' : 'max-w-[100px]',
                )}
              >
                {selected.name}
              </span>
            </>
          ) : (
            <>
              <Layers className={cn(isCompact ? 'h-3 w-3' : 'h-4 w-4', 'opacity-70')} />
              <span className="hidden sm:inline truncate">
                {isCompact ? 'Grupos' : 'Todos Grupos'}
              </span>
            </>
          )}
          <ChevronDown className={cn(isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3', 'opacity-50')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar grupo..." />
          <CommandList>
            <CommandEmpty>Nenhum grupo encontrado.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => handleSelect(null)}
                className="cursor-pointer"
              >
                <div
                  className={cn(
                    'mr-2 h-4 w-4 rounded-full border flex items-center justify-center',
                    value === null ? 'border-primary bg-primary' : 'border-muted',
                  )}
                >
                  {value === null && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                </div>
                <Layers className="h-4 w-4 mr-2 opacity-50" />
                <span>Todos Grupos</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Grupos ativos">
              {groups.map((g) => (
                <CommandItem
                  key={g.id}
                  value={g.name}
                  onSelect={() => handleSelect(g.id)}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      'mr-2 h-4 w-4 rounded-full border flex items-center justify-center',
                      value === g.id ? 'border-primary bg-primary' : 'border-muted',
                    )}
                  >
                    {value === g.id && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                  </div>
                  <span
                    className="h-3 w-3 rounded-full mr-2 shrink-0 border"
                    style={{ backgroundColor: g.color }}
                  />
                  <span className="truncate flex-1">{g.name}</span>
                  <span className="text-[10px] text-muted-foreground ml-2">
                    {g.inbox_count}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
